/**
 * TealerLab — Slider core (Swiper Custom Element loader)
 * =====================================================================
 * ONE slider engine for the whole theme. Sections never import Swiper
 * themselves: they render a native horizontal scroller, tag it, and this
 * module upgrades it to Swiper when it approaches the viewport.
 *
 * WHY IT WORKS THIS WAY
 * - `assets/tlr-swiper.min.js` (Swiper 11 custom element, ~183 KB min) is
 *   NEVER in the <head> and never render-blocking: it is dynamically
 *   imported the first time a tagged slider comes near the viewport. A page
 *   without a slider pays nothing at all; a page whose sliders stay below the
 *   fold only pays this file (~7 KB) and never downloads Swiper.
 * - Anti-CLS by construction: until Swiper is ready the markup IS the
 *   fallback (native `overflow-x` scroller), which is also the no-JS
 *   experience. Swiper is then configured FROM the fallback's computed style
 *   (slide widths, gap, edge padding) so the upgrade is pixel-identical and
 *   the two states cannot drift.
 *
 * ---------------------------------------------------------------------
 * HOW A SECTION ADOPTS THE SLIDER
 * ---------------------------------------------------------------------
 * INVARIANTS (violating either one shipped a real bug):
 * - a container whose media query never matches is never auto-mounted AND
 *   never re-observed after an unmount — manual mount()/unmount() only;
 * - a section that draws its own external controls (dots, arrows) keeps them
 *   functional BEFORE `tlr:slider:init` and AFTER `tlr:slider:destroy` — the
 *   native scroller is the fallback, controls must drive it too.
 * ---------------------------------------------------------------------
 * 1. Markup — keep the existing scroller, add the marker + the loader tag:
 *
 *      <div class="tlr-foo__track"
 *           data-tlr-slider
 *           data-tlr-slider-media="(max-width: 768px)"
 *           data-tlr-slider-autoplay="5000"
 *           data-tlr-slider-loop
 *           data-tlr-slider-prev="[data-tlr-foo-prev]"
 *           data-tlr-slider-next="[data-tlr-foo-next]">
 *        <div class="tlr-foo__slide">…</div>
 *        <div class="tlr-foo__slide">…</div>
 *      </div>
 *      {% render 'tlr-slider' %}
 *
 *    Direct children ARE the slides — including the `div.shopify-block`
 *    wrappers emitted by `content_for 'blocks'`. They receive the
 *    `swiper-slide` class at mount time, so style slides by class, never by
 *    tag: the container itself becomes a <swiper-container> element (its
 *    classes, id and data-attributes are carried over).
 *
 * 2. CSS — the fallback scroller, which is also the post-init geometry:
 *
 *      .tlr-foo__track {
 *        display: flex;
 *        overflow-x: auto;              // native swipe before/without JS
 *        scroll-snap-type: x mandatory;
 *        gap: 12px;                     // becomes Swiper `spaceBetween`
 *        padding-inline: 16px;          // becomes `slidesOffsetBefore/After`
 *        margin-inline: -16px;          // full-bleed, kept after init
 *        scrollbar-width: none;
 *      }
 *      .tlr-foo__track::-webkit-scrollbar { display: none; }
 *      .tlr-foo__track > * { flex: 0 0 65vw; scroll-snap-align: start; }
 *
 *    Slide widths stay CSS-driven (`slidesPerView: 'auto'`), identical before
 *    and after init. Hover effects MUST live in `@media (hover: hover)`: on a
 *    touch device a `:hover` transform sticks after a tap (recurring TLR bug).
 *
 *    FULL-BLEED IS THE MOBILE STANDARD: the rail spans
 *    the whole screen width (`margin-inline: -16px` when inside .tlr-container,
 *    nothing when the section is already full-width) with `padding-inline: 16px`
 *    and `scroll-padding-inline: 16px` so the resting slide aligns on the page
 *    gutter. Swiper's shadow `.swiper{overflow:hidden}` then clips at the SCREEN
 *    edge — slides visibly run under the edges while swiping. A rail left inside
 *    the container shows a 16px gutter clip: wrong.
 *
 * 3. Options (all optional, all on the container):
 *
 *    data-tlr-slider            required marker
 *    data-tlr-slider-media      only run while this media query matches, and
 *                               unmount (back to the plain scroller) when it
 *                               stops matching — that is how "slider on
 *                               mobile, CSS grid on desktop" is done
 *    data-tlr-slider-autoplay   delay in ms (0/absent = off). Forced off under
 *                               `prefers-reduced-motion`, paused off-viewport
 *                               and on mouse enter, never killed by a swipe
 *    data-tlr-slider-loop       loop, applied only when there are enough
 *                               slides (>= 3) — Swiper misbehaves below that
 *    data-tlr-slider-pagination shadow-DOM dots, drawn by the shared style in
 *                               assets/tlr-base.css (colours: --tlr-pagination-on/off
 *                               on the container)
 *    data-tlr-slider-prev/next  selector of an existing arrow, resolved inside
 *                               the section. Swiper then owns the click, the
 *                               disabled state (`.swiper-button-disabled`) and
 *                               hides them when everything fits
 *                               (`.swiper-button-lock`)
 *    data-tlr-slider-space      px between slides (default: computed column-gap)
 *    data-tlr-slider-options    raw Swiper params as JSON, merged last
 *
 * 4. Events (they bubble, so a section listens on its own root):
 *      tlr:slider:init     detail { swiper }  — Swiper took over
 *      tlr:slider:destroy  detail {}          — back to the native scroller
 *    A section whose own JS drives the scroller must stand down on init.
 *
 * WHAT THE LOADER WRITES ON THE CONTAINER (inline, restored on unmount):
 *   display:block; overflow:visible; padding-inline:0 (only when the edge
 *   padding is converted into slide offsets, i.e. when loop is off — Swiper
 *   does not support offsets in loop mode, so the padding is kept instead).
 *
 * i18n: a11y strings come from `snippets/tlr-slider.liquid`
 * (`data-tlr-slider-i18n`), never hardcoded here.
 */

const SWIPER_MODULE = '@theme/tlr-swiper';
const CONTAINER_SELECTOR = '[data-tlr-slider]';
// Generous on purpose: Swiper must be ready before the section is looked at.
const ROOT_MARGIN = '600px 0px';
const MIN_LOOP_SLIDES = 3;
const SLIDE_CLASS = 'swiper-slide';
// Inline styles Swiper leaves on the slides when it hands the DOM back.
const SWIPER_SLIDE_PROPS = ['width', 'height', 'margin-right', 'margin-left', 'margin-top', 'margin-bottom'];

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
/** element (fallback container OR swiper-container) -> slider record */
const records = new WeakMap();

let swiperPromise = null;
let viewObserver = null;
let lateObserver = null;

/** Load the Swiper bundle once. Resolves when <swiper-container> is defined. */
function loadSwiper() {
  if (!swiperPromise) {
    swiperPromise = import(SWIPER_MODULE).then(() => customElements.whenDefined('swiper-container'));
  }
  return swiperPromise;
}

// `| t` HTML-escapes its output, so a translated apostrophe reaches us as
// `&#39;`. Swiper writes these strings into aria-labels, where entities are NOT
// decoded — an accessible name would literally read "C&#39;est".
const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

function translations() {
  const holder = document.querySelector('script[data-tlr-slider-i18n]');
  try {
    const raw = JSON.parse(holder?.dataset.tlrSliderI18n || '{}');
    for (const [key, value] of Object.entries(raw)) {
      if (typeof value === 'string') raw[key] = value.replace(/&(amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m]);
    }
    return raw;
  } catch (_) {
    return {};
  }
}

function number(value, fallback) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Section scope, so `data-tlr-slider-prev` never reaches another section. */
function scopeOf(el) {
  return el.closest('.shopify-section') || document;
}

/**
 * Swiper params, built from the dataset AND from the fallback's computed
 * style: gap and edge padding are read from the CSS that is already on screen.
 */
function paramsFor(el, slideCount) {
  const data = el.dataset;
  const style = getComputedStyle(el);
  const strings = translations();
  const loop = data.tlrSliderLoop !== undefined && slideCount >= MIN_LOOP_SLIDES;
  // Swiper ignores slidesOffset* in loop mode: keep the CSS padding instead.
  const offsetBefore = loop ? 0 : number(style.paddingInlineStart, 0);
  const offsetAfter = loop ? 0 : number(style.paddingInlineEnd, 0);
  const autoplayDelay = number(data.tlrSliderAutoplay, 0);

  const params = {
    slidesPerView: 'auto',
    spaceBetween: number(data.tlrSliderSpace, number(style.columnGap, 0)),
    slidesOffsetBefore: offsetBefore,
    slidesOffsetAfter: offsetAfter,
    loop,
    rewind: !loop,
    watchOverflow: true,
    grabCursor: true,
    threshold: 4,
  };

  const a11y = {
    prevSlideMessage: strings.previousSlide,
    nextSlideMessage: strings.nextSlide,
    firstSlideMessage: strings.firstSlide,
    lastSlideMessage: strings.lastSlide,
    paginationBulletMessage: strings.goToSlide ? `${strings.goToSlide} {{index}}` : undefined,
  };
  for (const [key, value] of Object.entries(a11y)) {
    if (!value) delete a11y[key];
  }
  if (Object.keys(a11y).length) params.a11y = a11y;

  if (autoplayDelay > 0 && !reduceMotion.matches) {
    params.autoplay = { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true };
  }

  if (data.tlrSliderPagination !== undefined) {
    params.pagination = { enabled: true, clickable: true };
  }

  const scope = scopeOf(el);
  const prevEl = data.tlrSliderPrev ? scope.querySelector(data.tlrSliderPrev) : null;
  const nextEl = data.tlrSliderNext ? scope.querySelector(data.tlrSliderNext) : null;
  if (prevEl && nextEl) params.navigation = { prevEl, nextEl };

  if (data.tlrSliderOptions) {
    try {
      Object.assign(params, JSON.parse(data.tlrSliderOptions));
    } catch (_) {
      // Malformed JSON: keep the TLR defaults rather than dropping the slider.
    }
  }

  return params;
}

function copyAttributes(from, to) {
  for (const attribute of Array.from(from.attributes)) {
    to.setAttribute(attribute.name, attribute.value);
  }
}

/** Swap the fallback container for a <swiper-container>, attributes included. */
function promote(el) {
  const host = document.createElement('swiper-container');
  copyAttributes(el, host);
  host.setAttribute('init', 'false');
  while (el.firstChild) host.appendChild(el.firstChild);
  el.replaceWith(host);
  return host;
}

/** Reverse of promote(): hand the section back its plain scroller. */
function demote(host, record) {
  const el = document.createElement(record.tagName);
  copyAttributes(host, el);
  el.removeAttribute('init');
  el.removeAttribute('data-tlr-slider-ready');
  if (record.style) el.setAttribute('style', record.style);
  else el.removeAttribute('style');
  while (host.firstChild) el.appendChild(host.firstChild);
  for (const slide of Array.from(el.children)) {
    for (const name of Array.from(slide.classList)) {
      if (name.startsWith('swiper-')) slide.classList.remove(name);
    }
    slide.removeAttribute('data-swiper-slide-index');
    for (const property of SWIPER_SLIDE_PROPS) slide.style.removeProperty(property);
    if (!slide.getAttribute('style')) slide.removeAttribute('style');
  }
  host.replaceWith(el);
  return el;
}

function mount(el) {
  const record = records.get(el);
  if (!record || record.host) return;

  const slides = Array.from(el.children);
  if (!slides.length) return;

  const params = paramsFor(el, slides.length);
  record.style = el.getAttribute('style') || '';
  for (const slide of slides) slide.classList.add(SLIDE_CLASS);

  const host = el.tagName === 'SWIPER-CONTAINER' ? el : promote(el);
  records.set(host, record);
  record.host = host;

  // The fallback scroller properties Swiper takes over from here.
  host.style.display = 'block';
  host.style.overflow = 'visible';
  if (params.slidesOffsetBefore || params.slidesOffsetAfter) host.style.paddingInline = '0';

  Object.assign(host, params);
  try {
    host.initialize();
  } catch (_) {
    // Never leave a host upgraded but uninitialised: its empty shadow root
    // would hide the whole section. Roll back to the native scroller.
    unmount(host);
    return;
  }
  host.setAttribute('data-tlr-slider-ready', '');

  if (params.autoplay && host.swiper) {
    record.autoplayObserver = new IntersectionObserver((entries) => {
      const swiper = host.swiper;
      if (!swiper || swiper.destroyed || !swiper.autoplay) return;
      if (entries[0].isIntersecting) swiper.autoplay.start();
      else swiper.autoplay.stop();
    });
    record.autoplayObserver.observe(host);
  }

  host.dispatchEvent(new CustomEvent('tlr:slider:init', { bubbles: true, detail: { swiper: host.swiper } }));
  return host;
}

function unmount(host) {
  const record = records.get(host);
  if (!record || record.host !== host) return;

  record.autoplayObserver?.disconnect();
  record.autoplayObserver = null;
  // cleanStyles = false: Swiper would otherwise wipe the slides' own inline
  // styles (Liquid writes CSS variables there). We clear its own props below.
  if (host.swiper && !host.swiper.destroyed) host.swiper.destroy(true, false);

  const el = record.tagName === 'SWIPER-CONTAINER' ? host : demote(host, record);
  record.host = null;
  records.set(el, record);

  el.dispatchEvent(new CustomEvent('tlr:slider:destroy', { bubbles: true, detail: {} }));
  // Re-observe ONLY containers the auto-mount is allowed to touch. A container
  // whose media query never matches (manual-mount opt-out, e.g. the quick-buy
  // stage) must not re-enter the shared observer: its next intersection would
  // disconnect the observer and force-mount every other slider on the page.
  if (!record.media || record.media.matches) observe(el);
}

/**
 * Mount every eligible slider of the page at once — deliberately.
 * Defining <swiper-container> upgrades EVERY such element, and an upgraded but
 * uninitialised one has an empty shadow root: its content would vanish. So
 * once the bundle is in, nothing is left half-upgraded. Plain scrollers (not
 * <swiper-container> yet) are exempt and mount on approach.
 */
function mountAll() {
  for (const el of document.querySelectorAll(CONTAINER_SELECTOR)) {
    const record = records.get(el);
    if (!record || record.host) continue;
    if (record.media && !record.media.matches) continue;
    // A plain scroller is not touched by the upgrade: it waits until it nears the viewport.
    if (el.tagName !== 'SWIPER-CONTAINER' && !isNear(el)) {
      observeLate(el);
      continue;
    }
    mount(el);
  }
  // Safety net for a <swiper-container> authored without the TLR marker.
  for (const el of document.querySelectorAll('swiper-container:not([data-tlr-slider])')) {
    if (!el.swiper) el.initialize?.();
  }
}

function requestMount() {
  loadSwiper()
    .then(mountAll)
    .catch(() => {
      // Swiper unavailable (offline, import map not updated): the native
      // scroller keeps working — nothing to repair.
    });
}

function isNear(el) {
  const rect = el.getBoundingClientRect();
  const margin = parseFloat(ROOT_MARGIN);
  return rect.bottom > -margin && rect.top < window.innerHeight + margin;
}

/** Swiper is already loaded here: mount each late scroller on its own approach. */
function observeLate(el) {
  if (!lateObserver) {
    lateObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          lateObserver.unobserve(entry.target);
          const record = records.get(entry.target);
          if (record && !record.host && (!record.media || record.media.matches)) mount(entry.target);
        }
      },
      { rootMargin: ROOT_MARGIN }
    );
  }
  lateObserver.observe(el);
}

function observe(el) {
  if (!viewObserver) {
    viewObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        viewObserver.disconnect();
        viewObserver = null;
        requestMount();
      },
      { rootMargin: ROOT_MARGIN }
    );
  }
  viewObserver.observe(el);
}

function register(el) {
  if (records.has(el)) return;

  const query = el.dataset.tlrSliderMedia;
  const record = {
    host: null,
    media: query ? window.matchMedia(query) : null,
    tagName: el.tagName,
    style: '',
    autoplayObserver: null,
  };
  records.set(el, record);

  // An authored <swiper-container> must not self-initialise on upgrade:
  // its params are set here, right before initialize().
  if (el.tagName === 'SWIPER-CONTAINER') el.setAttribute('init', 'false');

  record.media?.addEventListener('change', (event) => {
    if (event.matches) requestMount();
    else if (record.host) unmount(record.host);
  });

  if (record.media && !record.media.matches) return;
  observe(el);
}

function scan(root = document) {
  const containers = root.querySelectorAll?.(CONTAINER_SELECTOR);
  if (!containers?.length) return;
  for (const el of containers) register(el);
}

scan();

// Theme editor: a re-rendered section brings back untouched fallback markup.
document.addEventListener('shopify:section:load', (event) => scan(event.target));

export { mount, unmount };
